import { get, writable } from 'svelte/store';
import Tab from '../components/editor/Tab';
const stores = {};
export function flattenTree(tree) {
    const flat = [tree];
    if (tree.children) {
        tree.children.forEach((child) => {
            flat.push(...flattenTree(child));
        });
    }
    return flat;
}
function apply(tree, callback) {
    callback(tree);
    if (tree.children) {
        tree.children.forEach((child) => {
            apply(child, callback);
        });
    }
}
function replaceByItem(tree, oldItem, newItem) {
    if (!tree.children) {
        return false;
    }
    for (const index in tree.children) {
        if (tree.children[index] === oldItem) {
            if (newItem === null) {
                tree.children.splice(index, 1);
            }
            else {
                tree.children.splice(index, 1, newItem);
            }
            return true;
        }
        const replaced = replaceByItem(tree.children[index], oldItem, newItem);
        if (replaced) {
            return replaced;
        }
    }
    return false;
}
function restoreState(projectId, tree) {
    const savedState = localStorage.getItem(`projectState${projectId}`);
    if (!savedState) {
        return {
            open: [],
            tabs: [],
        };
    }
    const parsed = JSON.parse(savedState);
    const newTabs = [];
    const flattened = flattenTree(tree);
    parsed.editorTabs.forEach((id) => {
        const treeItem = flattened.find((candidate) => {
            return candidate.id === id;
        });
        if (treeItem) {
            newTabs.push(new Tab(treeItem.id, treeItem.name, treeItem.path, treeItem.extension, id === parsed.activeEditorTab));
        }
    });
    return {
        open: parsed.open,
        tabs: newTabs,
    };
}
function saveState(state, projectId) {
    const editorTabs = [];
    let activeEditorTab = undefined;
    state.tabs.forEach((tab) => {
        editorTabs.push(tab.getId());
        if (tab.isActive()) {
            activeEditorTab = tab.getId();
        }
    });
    localStorage.setItem(`projectState${projectId}`, JSON.stringify({ open: state.open, editorTabs, activeEditorTab }));
}
function cleanupState(tree, state) {
    const flattened = flattenTree(tree);
    const availableIds = flattened.map((item) => {
        return item.id;
    });
    state.open.forEach((openId) => {
        if (availableIds.indexOf(openId) === -1) {
            state.open.splice(state.open.indexOf(openId), 1);
        }
    });
    const newTabs = [];
    let hasActiveTab = false;
    state.tabs.forEach((tab) => {
        const index = availableIds.indexOf(tab.getId());
        if (index !== -1) {
            newTabs.push(tab);
            if (tab.isActive()) {
                hasActiveTab = true;
            }
        }
    });
    state.tabs = newTabs;
    if (!hasActiveTab && state.tabs.length > 0) {
        state.tabs[0].setActive(true);
    }
}
function findTreeItemById(id, tree) {
    return flattenTree(tree).find((candidate) => {
        return candidate.id === id;
    });
}
function createTabFromId(id, active, tree) {
    const item = findTreeItemById(id, tree);
    return new Tab(item.id, item.name, item.path, item.extension, active);
}
export default function getProjectStore(projectId) {
    if (!stores[projectId]) {
        const { subscribe, set, update } = writable({ tree: undefined, state: { open: [], tabs: [] } });
        stores[projectId] = {
            subscribe,
            update,
            set,
            setOpen: (id, open) => {
                update((storeValue) => {
                    const index = storeValue.state.open.indexOf(id);
                    if (open && index === -1) {
                        storeValue.state.open.push(id);
                    }
                    else if (!open && index !== -1) {
                        storeValue.state.open.splice(index, 1);
                    }
                    saveState(storeValue.state, projectId);
                    return storeValue;
                });
            },
            setShownInEditor: (id, show) => {
                update((storeValue) => {
                    const index = storeValue.state.tabs.findIndex((candidate) => {
                        return candidate.getId() === id;
                    });
                    if (show) {
                        storeValue.state.tabs.forEach((tab) => {
                            if (tab.isActive() && tab.getId() !== id) {
                                tab.setActive(false);
                            }
                        });
                        if (index === -1) {
                            storeValue.state.tabs.push(createTabFromId(id, true, storeValue.tree));
                        }
                        else {
                            storeValue.state.tabs[index].setActive(true);
                        }
                    }
                    else if (!show && index !== -1) {
                        const needsNewActiveTab = storeValue.state.tabs[index].isActive();
                        storeValue.state.tabs.splice(index, 1);
                        if (needsNewActiveTab) {
                            if (storeValue.state.tabs[index]) {
                                storeValue.state.tabs[index].setActive(true);
                            }
                            else if (storeValue.state.tabs[index - 1]) {
                                storeValue.state.tabs[index - 1].setActive(true);
                            }
                            else if (storeValue.state.tabs[index + 1]) {
                                storeValue.state.tabs[index + 1].setActive(true);
                            }
                        }
                    }
                    saveState(storeValue.state, projectId);
                    return storeValue;
                });
            },
            closeTabsExcept: (id) => {
                update((storeValue) => {
                    const tab = storeValue.state.tabs.find((candidate) => {
                        return candidate.getId() === id;
                    });
                    if (tab) {
                        tab.setActive(true);
                        storeValue.state.tabs = [tab];
                    }
                    saveState(storeValue.state, projectId);
                    return storeValue;
                });
            },
            setSavedState: (id, saved) => {
                update((projectState) => {
                    const tab = projectState.state.tabs.find((candidate) => {
                        return candidate.getId() === id;
                    });
                    if (tab) {
                        tab.setSaved(saved);
                    }
                    return projectState;
                });
            },
            add: (newItem, parentPath) => {
                update((storeValue) => {
                    apply(storeValue.tree, (item) => {
                        if (item.path === parentPath) {
                            if (!item.children) {
                                item.children = [];
                            }
                            item.children.push(newItem);
                        }
                    });
                    return storeValue;
                });
            },
            replace: (oldItem, newTtem) => {
                update((storeValue) => {
                    replaceByItem(storeValue.tree, oldItem, newTtem);
                    return storeValue;
                });
            },
            removeItem: (item) => {
                update((storeValue) => {
                    replaceByItem(storeValue.tree, item, null);
                    return storeValue;
                });
            },
            replaceTree: (tree) => {
                update((storeValue) => {
                    storeValue.state = restoreState(projectId, tree);
                    if (storeValue.state.open.indexOf(tree.id) === -1) {
                        storeValue.state.open.push(tree.id);
                    }
                    cleanupState(tree, storeValue.state);
                    storeValue.tree = tree;
                    return storeValue;
                });
            },
            destroy: () => {
                saveState(get(stores[projectId]).state, projectId);
                stores[projectId] = undefined;
            }
        };
    }
    return stores[projectId];
}
